import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Icon } from "@/components/icon"
import {
  STATUS_ICON,
  STATUS_ICON_CLASS,
  STATUS_ROLE,
  type StatusType,
} from "@/lib/feedback/status"

/**
 * Inline Alert — SRAM Figma node 3744:53863 (CRCB2B-644).
 *
 * A boxed callout that sits INSIDE page content and is bound to the content it
 * concerns. Not to be confused with AlertBanner, which is a full-bleed bar spanning
 * the viewport, or with Toast, which is transient and floats. See the
 * `Feedback/Alert` style-guide node for when to reach for which.
 *
 * STRUCTURE (fixed by the design, which is why the header parts are props rather than
 * free children):
 *
 *     +------------------------------------------+
 *     | [icon]  Title            [actions]  [x]  |   header row
 *     |------------------------------------------|   hairline divider
 *     | children - description, product rows     |   content, only if children exist
 *     +------------------------------------------+
 *
 * The divider and the content area appear only when there are children, which is what
 * makes the compact single-row form (Figma 10648:110944, 500x64) fall out of the same
 * component rather than needing a second variant.
 *
 * TYPE IS CARRIED BY THE ICON ALONE. The title is `--text-color-primary` black in
 * every variant — verified on all three Intent variants (1976:19444 Attention,
 * 1979:19541 Warning, 1979:19556 Neutral), each of which binds exactly one extra
 * colour variable and binds it to the icon. Colouring the title too would be a
 * deviation, not an enhancement.
 *
 * BREAKING CHANGES from the stock shadcn Alert this replaces:
 *   - `variant="destructive"` → `variant="error"`, and `variant="default"` →
 *     `variant="info"`. The vocabulary is now the shared one (`@/lib/feedback/status`)
 *     so Alert, Toast and AlertBanner cannot drift apart.
 *   - The icon is supplied BY the component from the variant, not passed as a child.
 *     That guarantees the glyph always matches the type, which is what keeps the type
 *     legible without colour perception. Pass `icon` to override, `icon={null}` to
 *     suppress.
 *   - The header title is the `title` prop. `AlertTitle` / `AlertDescription` remain
 *     exported for composing the CONTENT area below the divider.
 *   - `role` now varies by severity instead of always being `alert`.
 *
 * ANNOUNCEMENT SEMANTICS: `role` comes from STATUS_ROLE — assertive `alert` for
 * `error`/`warning`, polite `status` for `info`/`success`. The previous blanket
 * `role="alert"` made every informational message interrupt whatever a screen reader
 * was mid-sentence on. Note a live region announces on CHANGE, so an alert present in
 * the initial render announces nothing at any politeness; static messages need to be
 * reachable through document order instead.
 *
 * DESIGN GAP: the Figma node defines only three Intents (Neutral, Warning, Attention).
 * `success` is required by CRCB2B-644's AC but has no design, so its treatment is
 * inferred from the Toast's success variant (`check_circle` on `--success-text`).
 * Raised with design and accepted as built — the value came from a token SRAM already had, which
 * is why inferring it was safe. See the `styleguide-notifications` design, Open Question 2.
 */

interface AlertProps extends Omit<React.ComponentProps<"div">, "title"> {
  /** Status from the shared feedback vocabulary. Defaults to neutral `info`. */
  variant?: StatusType
  /** Header text. Rendered `body/default-bold` 16/700 in `--foreground`. */
  title?: React.ReactNode
  /**
   * Overrides the variant's glyph. `null` suppresses the icon entirely — do that only
   * when something else in view already carries the type, since the glyph is what
   * makes the type readable without colour.
   */
  icon?: React.ReactNode | null
  /** Header-row actions, left of the close affordance. Use `AlertAction`. */
  actions?: React.ReactNode
  /**
   * Opt-in dismissal. Defaults to FALSE so that forgetting to think about it yields a
   * NON-dismissible alert — visible and reportable — rather than a blocking message a
   * user can wave away. A blocking alert simply does not set this.
   */
  dismissible?: boolean
  /**
   * Accessible name for the close affordance. Pass a translated string; the default is
   * only a safety net. `t('close', { ns: 'common' })` ships in every locale.
   */
  closeLabel?: string
  /** Called when the close affordance is activated. */
  onDismiss?: () => void
}

function Alert({
  className,
  variant = "info",
  title,
  icon,
  actions,
  dismissible = false,
  closeLabel = "Close",
  onDismiss,
  children,
  role,
  ...props
}: AlertProps) {
  const hasContent = children != null && children !== false
  const showIcon = icon !== null

  return (
    <div
      data-slot="alert"
      data-variant={variant}
      // An explicit `role` still wins, so a caller with a reason can override the
      // severity mapping — but the mapping is the default, not the exception.
      role={role ?? STATUS_ROLE[variant]}
      className={cn(
        // `surface-page` white fill with a 1px `border-color-default` outer border.
        // Plain `border`, not `border-ui`: border-ui applies to Card only, and Alert
        // takes Tailwind's 1px default like the other primitives.
        "bg-background text-foreground relative flex w-full flex-col rounded-ui border p-4",
        className
      )}
      {...props}
    >
      {/* Header row. Gap is --spacing-3 (12).
          `items-start` rather than `items-center`: the two-line example (10648:111355) keeps
          the icon and close button pinned to the FIRST line while the title grows downward,
          which centring would break by floating the icon to the middle of a 48px block.
          Single-line titles are centred against the 32px icon by the min-height on the title
          wrapper below — not by centring the row. */}
      <div data-slot="alert-header" className="flex items-start gap-3">
        {showIcon &&
          (icon ?? (
            <Icon
              name={STATUS_ICON[variant]}
              // icon/xl (32) — larger than the Toast's 24, per the node.
              size={32}
              // `data-part`, not `data-slot`: Icon sets `data-slot="icon"` AFTER its prop
              // spread, deliberately, because every component icon rule in the codebase
              // selects on it. A caller's own `data-slot` is silently dropped — `data-part`
              // is the documented way to name a specific icon (see icon/index.tsx).
              data-part="alert-icon"
              className={cn("shrink-0", STATUS_ICON_CLASS[variant])}
            />
          ))}
        {/* min-h-8 (32) matches the icon and the close button, so a single 24px line of
            title centres against them instead of sitting high — the misalignment that
            `items-center` on the row also fixes, but only for the single-line case. When the
            title wraps, this box simply grows past 32 and the icon stays on the first line.
            min-w-0 so long unbroken strings wrap instead of widening the row and pushing the
            close button out of reach. */}
        <div
          data-slot="alert-heading"
          className="flex min-h-8 min-w-0 flex-1 flex-col justify-center"
        >
          {title != null && <AlertTitle>{title}</AlertTitle>}
        </div>
        {actions}
        {dismissible && (
          <Button
            type="button"
            variant="ghost"
            // 32px box holding a 24px glyph — the Figma Icon Button in this row.
            size="icon-sm"
            aria-label={closeLabel}
            onClick={onDismiss}
            data-slot="alert-close"
            className="shrink-0"
          >
            <Icon name="close" fill={false} />
          </Button>
        )}
      </div>

      {/* Content area. The `border-color-hairline` divider is INTERNAL — a different
          token from the `border-color-default` outer border. It exists only when there
          is content to divide from the header. */}
      {hasContent && (
        <div
          data-slot="alert-content"
          className="border-border-subtle mt-4 flex flex-col gap-4 border-t pt-4"
        >
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * `body/default-bold` — 16 / 700 / 1.5, tracking 3%.
 *
 * The upstream `line-clamp-1` is deliberately gone: a clamped title silently truncates
 * the very message the alert exists to deliver, and the design has a two-line example.
 */
function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "text-foreground text-md font-bold leading-[1.5] tracking-[0.03em]",
        className
      )}
      {...props}
    />
  )
}

/** Supporting copy in the content area — `body/sm` 14 on `--supporting-foreground`. */
function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-supporting-foreground text-sm leading-[1.5] tracking-[0.03em] [&_p]:leading-[1.5]",
        className
      )}
      {...props}
    />
  )
}

/**
 * Header-row action slot. A named slot so callers stop styling ad-hoc buttons into the
 * row and the gap stays consistent. Put `Button`s inside — `size="sm"` matches the
 * 32px header rhythm.
 */
function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("flex shrink-0 items-center gap-2", className)}
      {...props}
    />
  )
}

/**
 * A referenced product or order inside the content area (Figma "Action Row").
 *
 * Layout: a 48px leading media slot, then title/description, then an optional trailing
 * action. This is the AC's "product reference content" — the alert body carrying an
 * image, name, availability, and related actions.
 *
 * IMAGES: pass a `<DynamicImage>` with `widths` as `media`, never a bare `<img>` —
 * without it there are no responsive sources and no DIS resizing. The primitive takes
 * a slot rather than an image URL precisely so this file stays free of commerce
 * concerns while the call site keeps using the project's image component.
 */
interface AlertRowProps extends React.ComponentProps<"div"> {
  /** 48x48 leading slot — a `<DynamicImage>`, an icon, or nothing. */
  media?: React.ReactNode
  /** Trailing action, e.g. "Remove" or "Choose another size". */
  action?: React.ReactNode
}

function AlertRow({
  className,
  media,
  action,
  children,
  ...props
}: AlertRowProps) {
  return (
    <div
      data-slot="alert-row"
      className={cn("flex items-center gap-4", className)}
      {...props}
    >
      {media && (
        <div
          data-slot="alert-row-media"
          // Fixed 48px box with the image inset to 40px, per the node.
          className="flex size-12 shrink-0 items-center justify-center [&_img]:size-10 [&_img]:object-cover"
        >
          {media}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      {action && (
        <div data-slot="alert-row-action" className="shrink-0">
          {action}
        </div>
      )}
    </div>
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction, AlertRow }
export type { AlertProps, AlertRowProps }
